import { http, createClient } from "./http";
import { env } from "../config/env";

const deliveryPartnerApi = createClient(env.DELIVERY_PARTNER_BASE_URL || env.AUTH_BASE_URL);

function normalizePartner(row = {}, presence = null) {
  return {
    id: row.id || row._id || "",
    partnerId: row.partnerId || row.partner_id || "",
    name: row.name || row.fullName || row.partnerName || "-",
    email: row.email || "-",
    phone: row.phone || row.mobile || "-",
    avatar: row.avatar || "",
    vehicleType: row.vehicleType || row.vehicle_type || "-",
    vehicleNumber: row.vehicleNumber || row.vehicle_number || "-",
    city: row.city || row.location?.city || row.zone || "-",
    hub: row.hub || "-",
    status: String(
      row.status || row.partnerStatus || (row.isActive ? "Active" : "Inactive")
    ).toUpperCase(),
    approvalStatus: String(
      row.approvalStatus ||
        row.approval_status ||
        row.onboardingStatus ||
        "PENDING"
    ).toUpperCase(),
    createdAt: row.createdAt || row.created_at || "",
    liveStatus: String(presence?.liveStatus || row.liveStatus || "OFFLINE").toUpperCase(),
    isOnline: Boolean(presence?.isOnline ?? row.isOnline ?? false),
    isAvailable: Boolean(presence?.isAvailable ?? row.isAvailable ?? false),
    isFresh: Boolean(presence?.isFresh ?? row.isFresh ?? false),
    activeTaskCount: Number(presence?.activeTaskCount ?? row.activeTaskCount ?? 0),
    currentTaskId: presence?.currentTaskId || row.currentTaskId || null,
    lastSeenAt: presence?.lastSeenAt || row.lastSeenAt || null,
    lastSeenAgeSeconds: presence?.lastSeenAgeSeconds ?? row.lastSeenAgeSeconds ?? null,
    batteryLevel: presence?.batteryLevel ?? row.batteryLevel ?? null,
    appVersion: presence?.appVersion || row.appVersion || null,
    raw: row,
  };
}

const PARTNER_FETCH_BATCH_SIZE = 500;
const MAX_PARTNER_FETCH_PAGES = 200;

function pickRawListPayload(res) {
  const rows =
    res?.data?.items ||
    res?.data?.partners ||
    res?.data?.rows ||
    res?.partners ||
    res?.items ||
    res?.results ||
    res?.data ||
    [];

  return Array.isArray(rows) ? rows : [];
}

function pickTotalCount(res, fallback = 0) {
  const value =
    res?.total ??
    res?.data?.total ??
    res?.pagination?.total ??
    res?.data?.pagination?.total;

  const total = Number(value);
  return Number.isFinite(total) && total >= 0 ? total : fallback;
}

function normalizeListRows(rows, presenceMap = new Map()) {
  return rows.map((row) => {
    const presence =
      presenceMap.get(String(row.id || row._id || "")) ||
      presenceMap.get(String(row.partnerId || row.partner_id || "")) ||
      null;

    return normalizePartner(row, presence);
  });
}

async function fetchAllPartnerRows(params = {}) {
  const query = { ...params };
  delete query.limit;
  delete query.offset;

  const firstResponse = await http("/api/admin/delivery-partners", {
    method: "GET",
    params: {
      ...query,
      limit: PARTNER_FETCH_BATCH_SIZE,
      offset: 0,
    },
  });

  const firstRows = pickRawListPayload(firstResponse);
  const total = pickTotalCount(firstResponse, firstRows.length);

  if (firstRows.length >= total || firstRows.length < PARTNER_FETCH_BATCH_SIZE) {
    return { response: firstResponse, rows: firstRows, total };
  }

  const offsets = [];
  for (
    let offset = PARTNER_FETCH_BATCH_SIZE;
    offset < total && offsets.length < MAX_PARTNER_FETCH_PAGES - 1;
    offset += PARTNER_FETCH_BATCH_SIZE
  ) {
    offsets.push(offset);
  }

  const responses = await Promise.all(
    offsets.map((offset) =>
      http("/api/admin/delivery-partners", {
        method: "GET",
        params: {
          ...query,
          limit: PARTNER_FETCH_BATCH_SIZE,
          offset,
        },
      })
    )
  );

  const combinedRows = [
    ...firstRows,
    ...responses.flatMap((response) => pickRawListPayload(response)),
  ];

  const uniqueRows = [];
  const seen = new Set();

  combinedRows.forEach((row, index) => {
    const key = String(
      row?.id || row?._id || row?.partnerId || row?.partner_id || `row-${index}`
    );

    if (seen.has(key)) return;
    seen.add(key);
    uniqueRows.push(row);
  });

  return {
    response: firstResponse,
    rows: uniqueRows,
    total,
  };
}

function normalizeProfile(row = {}) {
  const onboarding =
    row.onboarding && typeof row.onboarding === "object" ? row.onboarding : {};

  const documents =
    onboarding.documents && typeof onboarding.documents === "object"
      ? onboarding.documents
      : {};

  const address =
    onboarding.address && typeof onboarding.address === "object"
      ? onboarding.address
      : row.address && typeof row.address === "object"
        ? row.address
        : null;

  const emergencyContact =
    onboarding.emergencyContact && typeof onboarding.emergencyContact === "object"
      ? onboarding.emergencyContact
      : row.emergencyContact && typeof row.emergencyContact === "object"
        ? row.emergencyContact
        : null;

  return {
    id: row.id || row._id || "",
    partnerId: row.partnerId || row.partner_id || "",
    name: row.name || onboarding.name || "-",
    email: row.email || onboarding.email || "-",
    phone: row.phone || onboarding.phone || "-",

    avatar:
      row.avatar ||
      onboarding.avatar ||
      onboarding.profilePhoto?.url ||
      documents.profilePhoto?.url ||
      "",

    vehicleType:
      row.vehicleType ||
      onboarding.vehicleType ||
      row.vehicle_type ||
      "-",

    vehicleNumber:
      row.vehicleNumber ||
      onboarding.vehicleNumber ||
      row.vehicle_number ||
      "-",

    city: row.city || onboarding.city || address?.city || "-",
    hub: row.hub || onboarding.hub || "-",

    status: String(row.status || (row.isActive ? "ACTIVE" : "INACTIVE")).toUpperCase(),
    approvalStatus: String(row.approvalStatus || "NOT_SUBMITTED").toUpperCase(),

    isApproved: !!row.isApproved,
    approvalNote: row.approvalNote || "",
    approvedBy: row.approvedBy || "",
    approvedAt: row.approvedAt || "",

    createdAt: row.createdAt || "",
    updatedAt: row.updatedAt || "",

    fatherName: onboarding.fatherName || "-",
    motherName: onboarding.motherName || "-",
    aadhaarNumber: onboarding.aadhaarNumber || "-",
    panNumber: onboarding.panNumber || "-",
    dlNumber: onboarding.dlNumber || "-",
    rcNumber: onboarding.rcNumber || "-",
    bankAccount: onboarding.bankAccount || "-",
    bankIfsc: onboarding.bankIfsc || onboarding.ifsc || "-",
    bankHolderName: onboarding.bankHolderName || "-",

    orderSummary: row.orderSummary || row.summary || {},
    orders: Array.isArray(row.orders) ? row.orders : [],

    emergencyContact,
    address,

    profilePhoto: documents.profilePhoto || onboarding.profilePhoto || null,
    aadhaarDoc: documents.aadhaar || null,
    dlDoc: documents.dl || null,
    bankPassbookDoc: documents.bankPassbook || null,
    bikeRcDoc: documents.bikeRc || null,

    raw: row,
  };
}

export const DeliveryPartnersService = {
  async list(params = {}) {
    const [partnerResult, presenceRes] = await Promise.all([
      fetchAllPartnerRows(params),
      env.DELIVERY_PARTNER_BASE_URL
        ? deliveryPartnerApi.get("/api/admin/delivery-partners/presence", {
            params: { limit: 5000 },
          })
        : Promise.resolve({ items: [], summary: {} }),
    ]);

    const presenceRows = Array.isArray(presenceRes?.items) ? presenceRes.items : [];
    const presenceMap = new Map();
    presenceRows.forEach((item) => {
      if (item?.id) presenceMap.set(String(item.id), item);
      if (item?.partnerId) presenceMap.set(String(item.partnerId), item);
    });

    return {
      ...partnerResult.response,
      total: partnerResult.total,
      items: normalizeListRows(partnerResult.rows, presenceMap),
      liveSummary: presenceRes?.summary || {},
    };
  },

  async getPresenceSummary() {
    if (!env.DELIVERY_PARTNER_BASE_URL) {
      throw new Error("Delivery partner service URL is not configured");
    }
    return deliveryPartnerApi.get("/api/admin/delivery-partners/presence", {
      params: { limit: 5000 },
    });
  },

  async getById(id) {
    const res = await http(`/api/admin/delivery-partners/${id}`, {
      method: "GET",
    });

    const row = res?.partner || res?.data?.partner || res?.data || null;

    let orderInfo = null;
    try {
      if (env.DELIVERY_PARTNER_BASE_URL) {
        orderInfo = await deliveryPartnerApi.get(
          `/api/admin/delivery-partners/${id}/order-info`,
          { params: { limit: 20, offset: 0 } }
        );
      }
    } catch (err) {
      console.warn("Failed to load delivery partner order info", err);
    }

    const merged = row
      ? {
          ...row,
          orderSummary: orderInfo?.summary || row.orderSummary || {},
          orders: orderInfo?.orders || row.orders || [],
        }
      : null;

    return {
      ...res,
      partner: merged ? normalizeProfile(merged) : null,
    };
  },

  async create(body) {
    const formData = new FormData();

    Object.entries(body || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      if (key === "avatarFile") return;
      formData.append(key, value);
    });

    if (body?.avatarFile) {
      formData.append("avatar", body.avatarFile);
    }

    return http("/api/admin/delivery-partners", {
      method: "POST",
      body: formData,
    });
  },

  async update(id, body) {
    const formData = new FormData();

    Object.entries(body || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      if (key === "avatarFile") return;
      formData.append(key, value);
    });

    if (body?.avatarFile) {
      formData.append("avatar", body.avatarFile);
    }

    return http(`/api/admin/delivery-partners/${id}`, {
      method: "PATCH",
      body: formData,
    });
  },

  async remove(id) {
    return http(`/api/admin/delivery-partners/${id}`, {
      method: "DELETE",
      body: {
        reason: "Deleted by admin",
      },
    });
  },

  async updateStatus(id, { status, reason }) {
    return http(`/api/admin/delivery-partners/${id}/status`, {
      method: "PATCH",
      body: {
        status,
        reason,
      },
    });
  },

  async updateApproval(id, { status, note }) {
    return http(`/api/admin/delivery-partners/${id}/approval`, {
      method: "PATCH",
      body: {
        status,
        note: note || "",
      },
    });
  },

  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append("file", file);

    return http("/api/upload", {
      method: "POST",
      body: formData,
    });
  },
};